import React, { useState, useEffect } from 'react'
import noop from 'lodash/noop'
import { isLoaded, isLoading } from 'flux-entities'
import { Dropdown, IDropdownItem, Row, Col, TextInputOld, NumberInput,
         Button, SmartList, IColumnElement, IHeaderItem } from '@doseme/cohesive-ui'

import { TProps } from './types'
import { HOSPITALS_URL, } from '../../../../constants/urls/frontend'
import { TIMEZONES } from '../../../../constants/timezones'
import { FormPageLayout } from '../../../../components/FormPageLayout'
import { StyledForm } from '../../../../components/StyledForm'
import { TFormsValidity } from '../../types'
import { IHospitalToUpdate } from '../../../../types/hospital'
import { formatSubscriptionRows, splitCurrentAndExpiredSubscriptions } from '../utils'
import { ISubscription } from '../../../../types/subscription'

type TKey = 'instanceId' | 'installation' | 'name' | 'maxEnabledClinicians' | 'timezone'

const Edit: React.FC<TProps> = (props) => {
  const {
    hospitals,
    fetchHospitals,
    updateHospital,
    subscriptions,
    fetchSubscriptions,
    installations,
    fetchInstallations,
    plans,
    fetchPlans,
    history,
    match
  } = props

  const [showExpiredPlans, setShowExpiredPlans] = useState(false)

  const [copy, updateCopy] = useState<Pick<IHospitalToUpdate, 'timezone' | 'maxEnabledClinicians' | 'subscriptions' >>()

  const [error, updateError] = useState('')

  const [fieldsValidity, setFieldsValidity] = useState<TFormsValidity<TKey>>({
    instanceId: {
      touched: true,
      valid: true,
    },
    installation: {
      touched: true,
      valid: true,
    },
    name: {
      touched: true,
      valid: true,
    },
    maxEnabledClinicians: {
      touched: true,
      valid: true,
    },
    timezone: {
      touched: true,
      valid: true,
    }
  })

  const timezones: IDropdownItem[] = TIMEZONES.map(tzName => ({value: tzName }))

  useEffect(() => {
    if (!hospitals.touched)     { fetchHospitals() }
  }, [fetchHospitals, hospitals.touched])

  useEffect(() => {
    if (!installations.touched) { fetchInstallations() }
  }, [fetchInstallations, installations.touched])

  useEffect(() => {
    fetchSubscriptions(match.params.id)
  }, [fetchSubscriptions, match.params.id])

  useEffect(() => {
    if (!plans.touched) { fetchPlans() }
  }, [fetchPlans, plans.touched])

  useEffect(() => {
    const loadedHospital = hospitals.all[match.params.id]
    if (loadedHospital && subscriptions.all?.[match.params.id]) {
      updateCopy({
        timezone: loadedHospital.timezone,
        maxEnabledClinicians: loadedHospital.maxEnabledClinicians,
        subscriptions: subscriptions.all[match.params.id],
      })
    }
  }, [hospitals.all, updateCopy, match.params.id, subscriptions.all])

  const hospital = hospitals.all[match.params.id]

  if (!hospital && isLoaded(hospitals)) {
    return <div>No hospital was found with the id {match.params.id}</div>
  }

  if (
    ![installations, plans, subscriptions].every(isLoaded) ||
    !subscriptions.all[match.params.id]
  ) {
    return <div>Loading...</div>
  }

  const handleInput = (name: string, value: string, valid: boolean) => {
    if (!valid || !copy) {
      return
    }

    // tslint:disable-next-line:no-shadowed-variable
    const { maxEnabledClinicians, subscriptions, timezone } = copy
    updateCopy({ maxEnabledClinicians, subscriptions, timezone, [name]: value })
    setFieldsValidity({
      ...fieldsValidity,
      [name]: {
        touched: true,
        valid
      }
    })
  }

  const handleShowExpiredPlansToggled = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setShowExpiredPlans(evt.target.checked)
  }

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()

    if (!copy) {
      return
    }

    updateError('')

    // tslint:disable-next-line:no-shadowed-variable
    const { timezone, maxEnabledClinicians, subscriptions } = copy
    const { status, message } = await updateHospital({
      ...hospital,
      timezone,
      maxEnabledClinicians,
      subscriptions
    })

    if (status >= 400) {
      updateError(message)
      return
    }

    history.push(HOSPITALS_URL)
  }

  const columnContent = (sub: ISubscription): IColumnElement[] => [
    {
      name: 'planName',
      element: <span>{plans.all[sub.planId].type} - {plans.all[sub.planId].name}</span>,
      text: `${plans.all[sub.planId].type} - ${plans.all[sub.planId].name}`,
    },
    {
      name: 'start',
      element: <span>{sub.start.format('MMM DD, YYYY')}</span>,
      text: sub.start.format('MMM DD, YYYY'),
    },
    {
      name: 'expiry',
      element: <span>{sub.end.format('MMM DD, YYYY')}</span>,
      text: sub.end.format('MMM DD, YYYY'),
    },
  ]

  const {
    currentSubscriptions,
    expiredSubscriptions
  } = splitCurrentAndExpiredSubscriptions(subscriptions.all[match.params.id])

  const formValid = (Object.keys(fieldsValidity) as TKey[]).every(
    field => fieldsValidity[field].valid && fieldsValidity[field].touched)

  const cols: IHeaderItem[] = [
    {
      name: 'Plan Name',
      className: 'col-4'
    },
    {
      name: 'Start Date',
    },
    {
      name: 'Expiry Date',
    },
  ]

  return (
    <>
      <FormPageLayout
        title='Edit Hospital'
        subtitle={`${hospital.name} – ID: ${hospital.instanceId}`}
        handleBackClick={() => history.push(HOSPITALS_URL)}
      >
        <StyledForm handleSubmit={handleSubmit}>
          <h4 className='pr-2 mb-3'>Hospital Summary</h4>
          <Row>
            <Col>
              <TextInputOld
                name='instanceId'
                placeholder='Hospital ID'
                label='Hospital ID'
                defaultValue={hospital.instanceId.toString()}
                type='text'
                onBlur={(value: string, valid: boolean) => handleInput('instanceId', value, valid)}
                isRequired
                disabled
              />
            </Col>
            <Col>
              <TextInputOld
                name='name'
                placeholder='Hospital Name'
                label='Hospital Name'
                defaultValue={hospital.name}
                type='text'
                onBlur={(value: string, valid: boolean) => handleInput('name', value, valid)}
                isRequired
                disabled
              />
            </Col>
          </Row>
          <br />
          <Row>
            <Col>
              <TextInputOld
                name='installation'
                label='Installation'
                placeholder={installations.all[hospital.installation].code}
                defaultValue={installations.all[hospital.installation].code}
                type='text'
                onBlur={(value: string, valid: boolean) => handleInput('installation', value, valid)}
                isRequired
                disabled
              />
            </Col>
            <Col>
              <Dropdown
                fieldState='valid'
                label='Timezone'
                showSearchThreshold={0}
                data={timezones}
                placeholder={hospital.timezone}
                onSelect={(item: IDropdownItem) => handleInput( 'timezone', item.value, true)}
                value={copy ? copy.timezone : hospital.timezone}
              />
            </Col>
          </Row>
          <br />
          <br />

          <div className='mb-5'>
            <h4 className='co-h4'>Hospital Subscriptions</h4>
            <div id='subscriptions'>
              <SmartList
                cols={cols}
                data={formatSubscriptionRows(currentSubscriptions, columnContent)}
                textIfEmpty='This hospital has no active subscriptions.'
              />
              {showExpiredPlans && <SmartList
                header={false}
                cols={cols}
                data={formatSubscriptionRows(expiredSubscriptions, columnContent)}
              />}
            </div>
            <div className='d-flex width-100 justify-content-end align-items-center mt-2'>
              <input
                id='show-expired-plans'
                type='checkbox'
                checked={showExpiredPlans}
                onChange={handleShowExpiredPlansToggled}
              />
              <label className='mb-0 ml-1' htmlFor='show-expired-plans'><small>Show expired plans</small></label>
            </div>
          </div>

          <h4 className='co-h4'>Hospital Entitlements</h4>
          <NumberInput
            name='maxEnabledClinicians'
            label='Max. Enabled Clinicians Allowed'
            placeholder={hospital.maxEnabledClinicians.toString()}
            defaultValue={hospital.maxEnabledClinicians.toString()}
            data-test='max-clinicians'
            type='whole'
            handleBlur={(value, valid) => handleInput('maxEnabledClinicians', value, valid)}
            isRequired
            className='w-50'
          />
          <br />
          <hr />

          <div className='d-flex w-100 justify-content-end'>
            <Button
              type='submit'
              variant='primary'
              onClick={noop}
              disabled={!formValid}
              loading={isLoading(hospitals)}
            >
              Update
            </Button>
          </div>
        </StyledForm>

        {error}
      </FormPageLayout>
    </>
  )
}

export {
  Edit
}
